import { z } from 'zod';
import { adminUserSchema } from './auth.js';
export const MEETING_STATUSES = ['SCHEDULED', 'LIVE', 'ENDED'];
export const meetingStatusSchema = z.enum(MEETING_STATUSES);
/** Rôle de l'utilisateur courant sur une réunion. */
export const meetingRoleSchema = z.enum(['OWNER', 'CO_HOST', 'ADMIN']);
const MAX_EXPIRY_MS = 366 * 24 * 60 * 60 * 1000;
const isoDate = z.iso.datetime({ offset: true });
const meetingFields = {
    title: z.string().trim().min(1, 'Indiquez un titre.').max(120, '120 caractères maximum.'),
    /** Créneau indicatif (aucune invitation calendrier). */
    scheduledStart: isoDate.nullable(),
    scheduledEnd: isoDate.nullable(),
    /** Après cette date, plus aucun lien ne fonctionne et la salle est fermée. */
    expiresAt: isoDate,
    chatEnabled: z.boolean(),
    screenShareEnabled: z.boolean(),
};
function checkSchedule(value, ctx, requireFutureExpiry) {
    const start = value.scheduledStart ? Date.parse(value.scheduledStart) : null;
    const end = value.scheduledEnd ? Date.parse(value.scheduledEnd) : null;
    const expires = value.expiresAt ? Date.parse(value.expiresAt) : null;
    if (end !== null && start === null) {
        ctx.addIssue({ code: 'custom', path: ['scheduledStart'], message: 'Indiquez aussi le début.' });
    }
    if (start !== null && end !== null && end <= start) {
        ctx.addIssue({
            code: 'custom',
            path: ['scheduledEnd'],
            message: 'La fin doit suivre le début.',
        });
    }
    if (expires !== null) {
        if (requireFutureExpiry && expires <= Date.now()) {
            ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'Choisissez une date future.' });
        }
        if (expires > Date.now() + MAX_EXPIRY_MS) {
            ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'Un an maximum.' });
        }
        const reference = end ?? start;
        if (reference !== null && expires < reference) {
            ctx.addIssue({
                code: 'custom',
                path: ['expiresAt'],
                message: 'L’expiration doit suivre le créneau prévu.',
            });
        }
    }
}
export const meetingCreateSchema = z
    .object({
    ...meetingFields,
    coHostIds: z.array(z.uuid()).max(10, '10 co-hôtes maximum.').default([]),
})
    .superRefine((value, ctx) => {
    checkSchedule(value, ctx, true);
});
export const meetingUpdateSchema = z
    .object(meetingFields)
    .partial()
    .superRefine((value, ctx) => {
    checkSchedule(value, ctx, value.expiresAt !== undefined);
});
export const INVITATION_STATUSES = ['ACTIVE', 'REVOKED', 'EXPIRED'];
export const invitationStatusSchema = z.enum(INVITATION_STATUSES);
export const invitationSummarySchema = z.object({
    id: z.uuid(),
    label: z.string(),
    email: z.string().nullable(),
    status: invitationStatusSchema,
    expiresAt: isoDate,
    createdAt: isoDate,
    revokedAt: isoDate.nullable(),
    lastUsedAt: isoDate.nullable(),
});
const userRefSchema = adminUserSchema.pick({ id: true, displayName: true });
export const meetingSummarySchema = z.object({
    id: z.uuid(),
    title: z.string(),
    status: meetingStatusSchema,
    scheduledStart: isoDate.nullable(),
    scheduledEnd: isoDate.nullable(),
    expiresAt: isoDate,
    chatEnabled: z.boolean(),
    screenShareEnabled: z.boolean(),
    owner: userRefSchema,
    myRole: meetingRoleSchema,
    invitationCounts: z.object({ active: z.number().int(), total: z.number().int() }),
    createdAt: isoDate,
    endedAt: isoDate.nullable(),
});
export const meetingDetailSchema = meetingSummarySchema.extend({
    createdBy: userRefSchema,
    coHosts: z.array(adminUserSchema),
    invitations: z.array(invitationSummarySchema),
});
export const meetingListSchema = z.object({
    active: z.array(meetingSummarySchema),
    recent: z.array(meetingSummarySchema),
});
export const coHostAddSchema = z.object({ userId: z.uuid() });
const optionalEmail = z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? null : value), z.email('Adresse e-mail invalide.').max(254).nullable().optional());
export const invitationsCreateSchema = z.object({
    invitations: z
        .array(z.object({
        label: z.string().trim().min(1, 'Indiquez un libellé.').max(80, '80 caractères maximum.'),
        /** Facultatif, jamais utilisé pour un envoi automatique en V1. */
        email: optionalEmail,
        /** Par défaut : l'expiration de la réunion. */
        expiresAt: isoDate.optional(),
    }))
        .min(1, 'Ajoutez au moins un invité.')
        .max(50, '50 invitations maximum par lot.'),
});
/** Liens générés : affichés une seule fois, le secret n'est jamais stocké en clair. */
export const invitationsCreatedSchema = z.object({
    invitations: z.array(z.object({ id: z.uuid(), label: z.string(), url: z.url() })),
});
export const invitationsRevokeSchema = z.object({
    invitationIds: z.array(z.uuid()).min(1).max(100),
});
export const invitationsRevokedSchema = z.object({ revoked: z.number().int() });
//# sourceMappingURL=meetings.js.map